import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CompanyModel} from '../../../../../../libs/shared/core/src/models/api/company/companyModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {IntegrationTypeModel} from '../../../../../models/api/integrationTypesModel';

/*
 * Props.
 */

interface IntegrationInformationBannerProps {
  integration: IntegrationTypeModel;
  isAdmin: boolean;
  host: string;
  onEnableClick: () => void;
  onDismissClick: () => void;
  recordRequestedIntegration: () => void;
  company: CompanyModel;
  user: TeammateModel;
  openComposer: () => void;
}

/*
 * Style.
 */

const StyledInformationDiv = styled.div`
  display: grid;
  grid-column-gap: 10px;
  margin-top: 30px;
  border-radius: 16px;

  ${(p) => boxShadow(p.theme, `0px 1px 9px rgba(0, 0, 0, 0.08)`)};
`;

const StyledInformationHeader = styled.div`
  background-color: #656668;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  height: 32px;

  display: grid;
  grid-template-columns: auto auto auto 1fr auto auto;
  align-items: center;
`;

const StyledSuggestedText = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade20};

  margin-left: 10px;
`;

const StyledSpacerDiv = styled.div`
  flex-grow: 1;
`;

const StyledHeaderText = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade20};
`;

const StyledIntegrationName = styled.div`
  color: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  line-height: 19px;
`;

const StyledInformationBody = styled.div`
  background-color: ${(p) => p.theme.greys.shade70};
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;

  display: grid;
  grid-template-columns: 6fr 1fr auto;
  align-items: center;
`;

const StyledIntegrationDescription = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade20};
  line-height: 20px;

  align-self: center;
  margin: 9px 14px 9px 14px;
`;

const StyledIntegrationIcon = styled.img`
  border-radius: 23px;
  height: 46px;
  width: 46px;
  margin-left: 13px;
  margin-top: -30px;
  ${(p) => boxShadow(p.theme, `0px 2px 9px rgba(0, 0, 0, 0.3)`)};
`;

const StyledActionButton = styled(ChromelessButton)`
  margin: 0px 3px 0px 3px;
  padding: 3px 3px 3px 3px;
  color: ${(p) => p.theme.greys.shade40};
`;

const StyledActionDiv = styled.div`
  padding: 2px;
  border-radius: 50%;

  div:hover > && {
    background: #7b7d7f;
  }
`;

const StyledButton = styled(ChromelessButton)`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  line-height: 16px;
  align-items: center;
  display: flex;
  border-radius: 15px;
  height: 30px;
  margin-right: 16px;
  padding: 7px 12px;

  background-color: ${(p) => p.theme.foreground};

  &:hover {
    background-color: ${(p) => p.theme.greys.shade10};
  }
`;

/*
 * Component.
 */

export const IntegrationInformationBanner: FC<IntegrationInformationBannerProps> = (props) => {
  const theme = useUserTheme();
  const integrationIconUrl = `${props.host}/api/1/integration_types/${props.integration.id}/public/icons/card.svg`;

  const logIntegrationInformationBannerAction = (action: string) => {
    trackInterfaceEvent(InterfaceEventsEnum.INTEGRATION_INFORMATION_BANNER_ACTION, {
      integration: props.integration.id,
      action,
    });
  };

  const onDismissClick = () => {
    props.onDismissClick();
    logIntegrationInformationBannerAction('dismiss');
  };

  const getBannerValues = () => {
    if (props.isAdmin) {
      return {
        buttonTitle: <Trans id="integrationinformationbanner-enablebutton">Enable</Trans>,
        headerText: '',
        buttonAction: props.onEnableClick,
      };
    }

    return {
      headerText: <Trans id="integrationinformationbanner-approvalneeded">Admin approval needed</Trans>,
      buttonTitle: <Trans id="integrationinformationbanner-requestaccess">Request access</Trans>,
      buttonAction: async () => {
        logIntegrationInformationBannerAction('request');
        props.recordRequestedIntegration();
        props.openComposer();
      },
    };
  };

  const {buttonTitle, headerText, buttonAction} = getBannerValues();

  return (
    <StyledInformationDiv>
      <StyledInformationHeader>
        <StyledIntegrationIcon alt="integration-icon" src={integrationIconUrl} />
        <StyledSuggestedText>
          <Trans id="integrationinformationbanner-suggestedintegration">Suggested integration:</Trans>
          &nbsp;
        </StyledSuggestedText>
        <StyledIntegrationName>{props.integration.name}</StyledIntegrationName>
        <StyledSpacerDiv />
        <StyledHeaderText>{headerText}</StyledHeaderText>
        <StyledActionButton onClick={onDismissClick}>
          <StyledActionDiv>
            <Icon name="exit" color={theme.greys.white} size={16} />
          </StyledActionDiv>
        </StyledActionButton>
      </StyledInformationHeader>
      <StyledInformationBody>
        <StyledIntegrationDescription>{props.integration.description}</StyledIntegrationDescription>
        <StyledSpacerDiv />
        <StyledButton onClick={buttonAction}>{buttonTitle}</StyledButton>
      </StyledInformationBody>
    </StyledInformationDiv>
  );
};
